/**
 * AuthProvider - Syncs Better Auth's useSession() into the Zustand auth store
 *
 * Must be mounted inside a React tree (typically in _app.tsx layout route).
 * Handles:
 * - Session -> Zustand sync
 * - localStorage cache updates
 * - Avatar caching
 * - Visibility change session refresh
 * - Query cache invalidation on auth changes
 */

import { useEffect, useRef } from 'react';
import { useSession } from '@/api/auth-client';
import { useAuthStore, saveCachedAuth } from '@/stores/authStore';
import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import { setSentryUser } from '@/config/sentry';

const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 30_000;

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const session = useSession();
  const prevUserIdRef = useRef<string | null>(null);

  const isOnline = useAuthStore(state => state.isOnline);
  const setSessionData = useAuthStore(state => state.setSessionData);
  const setCachedUser = useAuthStore(state => state.setCachedUser);
  const setCachedAvatarUrl = useAuthStore(state => state.setCachedAvatarUrl);

  // A 401 is the server saying there is no session. Anything else (429, 5xx,
  // network) means we could not find out, and the cached user stays in force.
  const transientError = !!session.error && session.error.status !== 401;

  // Sync Better Auth session into Zustand store
  useEffect(() => {
    const rawUser = session.data?.user;
    const user = rawUser ? { ...rawUser, image: rawUser.image ?? undefined } : null;
    const loading = session.isPending;
    const refetch =
      session.refetch ?
        async (options?: { disableCookieCache?: boolean }) => {
          await session.refetch(options ? { query: options } : undefined);
        }
      : null;

    setSessionData(user, loading, refetch, transientError);
    setSentryUser(user ? { id: user.id, email: user.email, name: user.name } : null);
  }, [session, transientError, setSessionData]);

  // Retry a failed check with backoff so a rate-limited or offline-ish page
  // load converges on the real session instead of living on the cache
  const retryDelayRef = useRef(RETRY_BASE_MS);
  const { error: sessionError, refetch: refetchSession } = session;
  useEffect(() => {
    if (!transientError) {
      retryDelayRef.current = RETRY_BASE_MS;
      return;
    }
    // Wait for the online event to reschedule rather than firing into the void
    if (!isOnline) return;
    const delay = retryDelayRef.current;
    retryDelayRef.current = Math.min(delay * 2, RETRY_MAX_MS);
    const timer = setTimeout(() => refetchSession?.(), delay);
    return () => clearTimeout(timer);
  }, [transientError, sessionError, refetchSession, isOnline]);

  // Cache user data when session is fetched (only when online)
  useEffect(() => {
    const loading = session.isPending;

    if (!isOnline) return;

    const cacheRawUser = session.data?.user;
    const cacheUser =
      cacheRawUser ? { ...cacheRawUser, image: cacheRawUser.image ?? undefined } : null;

    if (cacheUser) {
      saveCachedAuth(cacheUser);
      setCachedUser(cacheUser);

      // Cache avatar for offline use (only when user changes)
      if (cacheUser.image && cacheUser.id && cacheUser.id !== prevUserIdRef.current) {
        prevUserIdRef.current = cacheUser.id;
        const { id, image } = cacheUser;
        void import('@/primitives/avatarCache.js')
          .then(({ fetchAndCacheAvatar }) => fetchAndCacheAvatar(id, image))
          .then((dataUrl: string | null) => {
            if (dataUrl) setCachedAvatarUrl(dataUrl);
          })
          .catch(err => {
            // Let the next session update retry this user.
            if (prevUserIdRef.current === id) prevUserIdRef.current = null;
            console.warn('[auth] Failed to cache avatar:', err);
          });
      }
    } else if (!loading && !transientError) {
      saveCachedAuth(null);
      setCachedUser(null);
    }
  }, [
    session.data,
    session.isPending,
    transientError,
    isOnline,
    setCachedUser,
    setCachedAvatarUrl,
  ]);

  // Visibility change: refresh session when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === 'visible' &&
        !session.isPending &&
        navigator.onLine &&
        session.refetch
      ) {
        try {
          await session.refetch();
          await new Promise(resolve => setTimeout(resolve, 100));

          const currentUser = session.data?.user;
          if (currentUser?.id) {
            await queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
            await queryClient.invalidateQueries({
              queryKey: queryKeys.projects.list(currentUser.id),
            });
          } else {
            queryClient.removeQueries({ queryKey: queryKeys.projects.all });
          }
        } catch (err) {
          console.warn('[auth] Failed to refresh session on visibility change:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [session]);

  return <>{children}</>;
}
